'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { Mail } from 'lucide-react';

const MenuBar = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const pathname = usePathname();

  // Helper function to check if current path matches navigation item
  const isActive = (href: string) => {
    if (href === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(href);
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isClient]);

  // Close mobile menu when pathname changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // Calculate width based on scroll position
  const containerWidth = Math.max(800, 1200 - scrollY * 0.5);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="sticky inset-x-0 top-2 z-40 mt-4 w-full">
      <div
        className="dark:bg-secondary/5 relative z-[60] mx-auto hidden w-full max-w-7xl flex-row items-center justify-between self-start rounded-full bg-transparent px-4 py-2 lg:flex"
        style={{
          width: `${containerWidth}px`,
          backdropFilter: 'blur(12px)',
          //
          transition: 'width 0.1s ease-out',
        }}
      >
        <Link
          href="/"
          className="bg-background border-primary hover:bg-primary/10 relative z-20 mr-4 flex cursor-pointer items-center space-x-2 rounded-full border p-2 transition-all duration-300 hover:scale-105 hover:border-blue-400"
        >
          <Image src="/images/EJ.svg" alt="logo" width="35" height="35" />
        </Link>
        <div className="text-md absolute inset-0 hidden flex-1 flex-row items-center justify-center space-x-2 font-medium text-zinc-600 transition duration-200 hover:text-zinc-800 lg:flex lg:space-x-2">
          <Link
            href="/"
            className={`relative px-4 py-2 transition-all duration-300 ${
              isActive('/')
                ? 'from-primary to-primary/60 rounded-full bg-gradient-to-b text-white'
                : 'hover:bg-primary text-neutral-600 hover:rounded-full dark:text-neutral-300 hover:dark:text-white'
            }`}
          >
            <span className="relative z-20">Home</span>
          </Link>
          <Link
            href="/about"
            className={`relative px-4 py-2 transition-all duration-300 ${
              isActive('/about')
                ? 'from-primary to-primary/60 rounded-full bg-gradient-to-b text-white'
                : 'hover:bg-primary text-neutral-600 hover:rounded-full dark:text-neutral-300 hover:dark:text-white'
            }`}
          >
            <span className="relative z-20">About</span>
          </Link>
          <Link
            href="/projects"
            className={`relative px-4 py-2 transition-all duration-300 ${
              isActive('/projects')
                ? 'from-primary to-primary/60 rounded-full bg-gradient-to-b text-white'
                : 'hover:bg-primary text-neutral-600 hover:rounded-full dark:text-neutral-300 hover:dark:text-white'
            }`}
          >
            <span className="relative z-20">Projects</span>
          </Link>
          <Link
            href="/case-studies"
            className={`relative px-4 py-2 transition-all duration-300 ${
              isActive('/case-studies')
                ? 'from-primary to-primary/60 rounded-full bg-gradient-to-b text-white'
                : 'hover:bg-primary text-neutral-600 hover:rounded-full dark:text-neutral-300 hover:dark:text-white'
            }`}
          >
            <span className="relative z-20">Case Studies</span>
          </Link>
          <Link
            href="/contact"
            className={`relative px-4 py-2 transition-all duration-300 ${
              isActive('/contact')
                ? 'from-primary to-primary/60 rounded-full bg-gradient-to-b text-white'
                : 'hover:bg-primary text-neutral-600 hover:rounded-full dark:text-neutral-300 hover:dark:text-white'
            }`}
          >
            <span className="relative z-20">Contact</span>
          </Link>
        </div>
        <div className="flex items-center gap-4">
          <Link
            href="https://www.linkedin.com/in/ejmalek"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:bg-foreground/70 relative inline-block cursor-pointer rounded-sm bg-transparent transition-all duration-300"
          >
            <img
              src="/images/LinkedIn.svg"
              alt="linkedin"
              width="30"
              height="30"
              className="h-6 w-6"
            />
          </Link>
        </div>
      </div>

      {/* Mobile Menu Header */}
      <div className="relative z-50 mx-auto flex w-full max-w-[calc(100vw-2rem)] flex-col items-center justify-between bg-transparent px-0 py-2 lg:hidden">
        <div className="flex w-full flex-row items-center justify-between">
          <Link
            href="/"
            className="relative z-20 mr-4 flex items-center space-x-2 px-2 py-1"
          >
            <Image
              src="/images/EJ.svg"
              alt="logo"
              width="40"
              height="40"
              className="bg-background border-primary rounded-full border p-1"
            />
          </Link>
          <button
            onClick={toggleMobileMenu}
            className="hover:bg-accent/10 rounded-lg p-2 text-black transition-colors dark:text-white"
            aria-label="Toggle mobile menu"
          >
            <AnimatePresence mode="wait">
              {isMobileMenuOpen ? (
                <motion.svg
                  key="close"
                  initial={{ opacity: 0, rotate: -90, scale: 0.8 }}
                  animate={{ opacity: 1, rotate: 0, scale: 1 }}
                  exit={{ opacity: 0, rotate: 90, scale: 0.8 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-x"
                >
                  <motion.path
                    d="M18 6 6 18"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                  />
                  <motion.path
                    d="m6 6 12 12"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                  />
                </motion.svg>
              ) : (
                <motion.svg
                  key="menu"
                  initial={{ opacity: 0, rotate: 90, scale: 0.8 }}
                  animate={{ opacity: 1, rotate: 0, scale: 1 }}
                  exit={{ opacity: 0, rotate: -90, scale: 0.8 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-menu"
                >
                  <motion.path
                    d="M4 6h16"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.2 }}
                  />
                  <motion.path
                    d="M4 12h16"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.2, delay: 0.05 }}
                  />
                  <motion.path
                    d="M4 18h16"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.2, delay: 0.1 }}
                  />
                </motion.svg>
              )}
            </AnimatePresence>
          </button>
        </div>
      </div>

      {/* Mobile Menu Backdrop */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
            onClick={closeMobileMenu}
          />
        )}
      </AnimatePresence>

      {/* Mobile Menu Content */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{
              duration: 0.2,
              ease: 'easeOut',
            }}
            className="relative z-50 mx-auto w-full max-w-[calc(100vw-2rem)] lg:hidden"
          >
            <motion.div
              className="bg-card/95 border-border absolute inset-x-0 top-0 z-50 mx-4 flex w-full flex-col items-start justify-start gap-4 rounded-xl border px-6 py-8 shadow-2xl backdrop-blur-md"
              initial={{ y: -10 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2 }}
                className="w-full"
              >
                <Link
                  href="/"
                  onClick={closeMobileMenu}
                  className={`relative block w-full rounded-lg px-4 py-3 text-lg font-medium transition-all duration-200 ${
                    isActive('/')
                      ? 'bg-primary/10 text-primary border-primary/20 border'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                  }`}
                >
                  <span className="block">Home</span>
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: 0.05 }}
                className="w-full"
              >
                <Link
                  href="/about"
                  onClick={closeMobileMenu}
                  className={`relative block w-full rounded-lg px-4 py-3 text-lg font-medium transition-all duration-200 ${
                    isActive('/about')
                      ? 'bg-primary/10 text-primary border-primary/20 border'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                  }`}
                >
                  <span className="block">About</span>
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: 0.1 }}
                className="w-full"
              >
                <Link
                  href="/projects"
                  onClick={closeMobileMenu}
                  className={`relative block w-full rounded-lg px-4 py-3 text-lg font-medium transition-all duration-200 ${
                    isActive('/projects')
                      ? 'bg-primary/10 text-primary border-primary/20 border'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                  }`}
                >
                  <span className="block">Projects</span>
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: 0.15 }}
                className="w-full"
              >
                <Link
                  href="/case-studies"
                  onClick={closeMobileMenu}
                  className={`relative block w-full rounded-lg px-4 py-3 text-lg font-medium transition-all duration-200 ${
                    isActive('/case-studies')
                      ? 'bg-primary/10 text-primary border-primary/20 border'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                  }`}
                >
                  <span className="block">Case Studies</span>
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: 0.2 }}
                className="w-full"
              >
                <Link
                  href="/contact"
                  onClick={closeMobileMenu}
                  className={`relative block w-full rounded-lg px-4 py-3 text-lg font-medium transition-all duration-200 ${
                    isActive('/contact')
                      ? 'bg-primary/10 text-primary border-primary/20 border'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                  }`}
                >
                  <span className="block">Contact</span>
                </Link>
              </motion.div>

              <div className="border-border/50 w-full border-t pt-4">
                <motion.div
                  className="flex w-full flex-col gap-3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.25 }}
                >
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full"
                  >
                    <Link
                      href="/contact"
                      onClick={closeMobileMenu}
                      className="border-border text-foreground hover:bg-accent/10 hover:border-accent relative block w-full cursor-pointer rounded-lg border bg-transparent px-4 py-3 text-center text-sm font-bold transition-all duration-300"
                    >
                      <span className="flex items-center justify-center gap-2">
                        <Mail className="h-4 w-4" />
                        Contact
                      </span>
                    </Link>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full"
                  >
                    <Link
                      href="https://linkedin.com/in/ejmalek"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={closeMobileMenu}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 relative block w-full cursor-pointer items-center justify-center space-x-2 rounded-lg px-4 py-3 text-center text-sm font-bold transition-all duration-300"
                    >
                      <img
                        src="/images/LinkedIn.svg"
                        alt="LinkedIn"
                        width={16}
                        height={16}
                        className="brightness-0 invert filter"
                      />
                      <span>LinkedIn</span>
                    </Link>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MenuBar;
